import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PhaseIndicator } from "@/components/PhaseIndicator";
import {
  HouseholdMemberForm,
  HouseholdMember,
} from "@/components/HouseholdMemberForm";
import { Navbar } from "@/components/Navbar";

const EMERGENCY_TYPES = [
  "Earthquake",
  "Flood",
  "Hurricane",
  "Wildfire",
  "Tornado",
  "Winter Storm",
  "Power Outage",
  "Other",
];

export default function InterviewBasics() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Basic form state
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("United States");
  const [emergencyTypes, setEmergencyTypes] = useState<string[]>([]);
  
  // Emergency contact
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");

  // Household members
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([
    {
      id: crypto.randomUUID(),
      name: "",
      age: "",
      health_conditions: "",
      special_needs: "",
    },
  ]);

  const addHouseholdMember = () => {
    setHouseholdMembers([
      ...householdMembers,
      {
        id: crypto.randomUUID(),
        name: "",
        age: "",
        health_conditions: "",
        special_needs: "",
      },
    ]);
  };

  const updateHouseholdMember = (
    id: string,
    field: keyof HouseholdMember,
    value: string
  ) => {
    setHouseholdMembers(
      householdMembers.map((member) =>
        member.id === id ? { ...member, [field]: value } : member
      )
    );
  };

  const removeHouseholdMember = (id: string) => {
    setHouseholdMembers(householdMembers.filter((member) => member.id !== id));
  };

  const toggleEmergencyType = (type: string) => {
    setEmergencyTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!name || !address || !city || !state || !country) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (emergencyTypes.length === 0) {
        toast({
          title: "Missing Information",
          description: "Please select at least one emergency type",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Validate household members
      const validMembers = householdMembers.filter(
        (m) => m.name.trim() && m.age.trim()
      );
      if (validMembers.length === 0) {
        toast({
          title: "Missing Information",
          description: "Please add at least one household member",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!emergencyContactName || !emergencyContactPhone) {
        toast({
          title: "Missing Information",
          description: "Please provide emergency contact information",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create interview with Phase 1 data
      const { data: interview, error } = await supabase
        .from("interviews")
        .insert({
          user_id: user.id,
          name,
          location: {
            address,
            city,
            state,
            country,
          },
          household_size: validMembers.length,
          household_members: validMembers.map((m) => ({
            name: m.name,
            age: parseInt(m.age),
            health_conditions: m.health_conditions || null,
            special_needs: m.special_needs || null,
          })),
          emergency_contact: {
            name: emergencyContactName,
            phone: emergencyContactPhone,
          },
          emergency_types: emergencyTypes,
          property_type: "unknown", // Will be filled in Phase 2
          phase_completed: 1,
          status: "in_progress",
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Basic Info Saved",
        description: "Let's continue with your personalized interview",
      });

      // Navigate to Phase 2 (AI Interview)
      navigate("/chat", { state: { interviewId: interview.id } });
    } catch (error: any) {
      console.error("Error saving basic info:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto max-w-3xl px-4 py-8">
        <PhaseIndicator currentPhase={1} />

        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold">Basic Information</h1>
          <p className="mt-2 text-muted-foreground">
            Let's start with the essentials. This should only take a minute.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Info */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold">Personal Information</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold">Location</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main Street"
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="San Francisco"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State/Province *</Label>
                  <Input
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="California"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Emergency Types */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold">
              Primary Emergency Types *
            </h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Select the emergencies most relevant to your area
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {EMERGENCY_TYPES.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={type}
                    checked={emergencyTypes.includes(type)}
                    onCheckedChange={() => toggleEmergencyType(type)}
                  />
                  <Label htmlFor={type} className="cursor-pointer">
                    {type}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Household Members */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Household Members *</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addHouseholdMember}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Person
              </Button>
            </div>
            <div className="space-y-4">
              {householdMembers.map((member, index) => (
                <HouseholdMemberForm
                  key={member.id}
                  member={member}
                  index={index}
                  onUpdate={updateHouseholdMember}
                  onRemove={removeHouseholdMember}
                  canRemove={householdMembers.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-xl font-semibold">Emergency Contact *</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Someone outside your household who can help in an emergency
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="emergency-name">Contact Name *</Label>
                <Input
                  id="emergency-name"
                  value={emergencyContactName}
                  onChange={(e) => setEmergencyContactName(e.target.value)}
                  placeholder="Jane Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="emergency-phone">Contact Phone *</Label>
                <Input
                  id="emergency-phone"
                  type="tel"
                  value={emergencyContactPhone}
                  onChange={(e) => setEmergencyContactPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={loading}>
            {loading ? "Saving..." : "Continue to AI Interview"}
          </Button>
        </form>
      </div>
    </div>
  );
}
